// ============================================================
// Datei: frontend/src/services/api.ts
// Trading Engine v2.2 — KOMPLETT, alle Exports
// ============================================================
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// ============================================================
// Auth Token Interceptor — automatisch Bearer Token senden
// ============================================================
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token abgelaufen → Logout
      const currentPath = window.location.pathname;
      if (currentPath !== '/login' && currentPath !== '/welcome') {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        window.location.href = '/welcome';
      }
    }
    return Promise.reject(error);
  }
);

// ============================================================
// Error Helper — wird von Accounts.tsx importiert
// ============================================================
export function getErrorMessage(error: any): string {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.message || error.response?.data || error.message || 'Unbekannter Fehler';
  }
  if (error instanceof Error) return error.message;
  return String(error);
}

// ============================================================
// Types
// ============================================================
export interface Account {
  id: string;
  name: string;
  balance: number;
  is_active: boolean;
  risk_pct?: number;
  created_at: string;
  updated_at: string;
}

export interface Trade {
  id: string;
  account_id: string;
  trade_date: string;
  asset: string;
  session: string;
  direction?: string;
  pnl: number;
  equity_before: number;
  equity_after: number;
  return_pct: number;
  drawdown: number;
  risk_reward?: number;
  duration_mins: number;
  dd_duration_mins?: number;
  candlesbeforefill?: number;
  delta?: number;
  delta_fill?: number;
  sl_distance?: number;
  tp_distance?: number;
  volume_15m?: number;
  volume_fill?: number;
  mae?: number;
  mfi?: number;
  aligned?: boolean;
  mae_pct_sl?: number;
  entry_volume?: number;
  start_capital: number;
  comment?: string;
  tags: string[];
  custom_fields: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface TradingMetrics {
  total_trades: number;
  win_rate: number;
  sharpe_ratio: number;
  modified_sharpe: number;
  max_drawdown: number;
  /** DD Duration in MINUTEN */
  max_drawdown_duration: number;
  avg_win: number;
  avg_loss: number;
  /** Dollar Expectancy */
  expectancy: number;
  /** R-Multiple Expectancy z.B. +1.25 */
  expectancy_r: number;
  /** Durchschnittliches RR */
  avg_rr: number;
  avg_rr_wins: number;
  avg_rr_losses: number;
  trades_with_rr: number;
  profit_factor: number;
  payoff_ratio: number;
  kelly_criterion: number;
  calmar_ratio: number;
  recovery_factor: number;
  start_capital: number;
  current_equity: number;
  net_pnl: number;
  total_return_pct: number;
}

export interface EquityCurvePoint {
  trade_date: string;
  equity: number;
  cumulative_return: number;
}

export interface JournalEntry {
  id: string;
  trade_id: string | null;
  account_id: string;
  title: string;
  content: string;
  images: string[];
  tags: string[];
  created_at: string;
  updated_at: string;
}

export interface MlAnalysis {
  account_id: string;
  total_trades_analyzed: number;
  best_asset: {
    asset: string;
    win_rate: number;
    avg_pnl: number;
    total_trades: number;
    sharpe: number;
    all_assets: Array<{
      asset: string; win_rate: number; avg_pnl: number;
      total_trades: number; total_pnl: number; sharpe: number;
    }>;
  };
  best_session: {
    session: string;
    win_rate: number;
    avg_pnl: number;
    total_trades: number;
    all_sessions: Array<{
      session: string; win_rate: number; avg_pnl: number;
      total_trades: number; total_pnl: number;
    }>;
  };
  best_rr_range: {
    optimal_min: number; optimal_max: number;
    win_rate_in_range: number; avg_pnl_in_range: number;
    ranges: Array<{
      range_label: string; min_rr: number; max_rr: number;
      win_rate: number; avg_pnl: number; trade_count: number;
    }>;
  };
  custom_field_insights: Array<{
    field_name: string; correlation_with_pnl: number;
    optimal_range: [number, number] | null;
    best_value: number | null; avg_pnl_at_best: number; description: string;
  }>;
  tag_performance: Array<{
    tag: string; trade_count: number; win_rate: number;
    avg_pnl: number; total_pnl: number; sharpe: number;
  }>;
  recommendations: string[];
  optimal_trade_profile: {
    best_asset: string; best_session: string; best_rr_range: string;
    best_tags: string[]; best_custom_field_combos: string[];
    expected_win_rate: number; expected_avg_pnl: number;
  };
}

// ============================================================
// Account API
// ============================================================
export const accountsApi = {
  list: () => api.get<Account[]>('/accounts'),
  get: (id: string) => api.get<Account>(`/accounts/${id}`),
  create: (data: { name: string; balance: number; risk_pct?: number }) =>
    api.post<Account>('/accounts', data),
  update: (id: string, data: { name?: string; balance?: number; risk_pct?: number }) =>
    api.put<Account>(`/accounts/${id}`, data),
  delete: (id: string) => api.delete(`/accounts/${id}`),
  activate: (id: string) => api.post<Account>(`/accounts/${id}/activate`),
};

// ============================================================
// Trades API
// ============================================================
export const tradesApi = {
  list: (params?: {
    account_id?: string;
    tag?: string;
    asset?: string;
    session?: string;
    search?: string;
  }) => api.get<Trade[]>('/trades', { params }),

  get: (id: string) => api.get<Trade>(`/trades/${id}`),

  create: (data: {
    account_id: string;
    trade_date?: string;
    asset: string;
    session: string;
    direction?: string;
    pnl: number;
    risk_reward?: number;
    duration_mins: number;
    dd_duration_mins?: number;
    candlesbeforefill?: number;
    delta?: number;
    delta_fill?: number;
    sl_distance?: number;
    tp_distance?: number;
    volume_15m?: number;
    volume_fill?: number;
    mae?: number;
    mfi?: number;
    mae_pct_sl?: number;
    entry_volume?: number;
    entry_price?: number;
    stop_loss?: number;
    take_profit?: number;
    risk_pct?: number;
    risk_amount?: number;
    contracts?: number;
    strategy?: string;
    market_structure?: string;
    start_capital: number;
    comment?: string;
    tags?: string[];
    custom_fields?: Record<string, any>;
  }) => api.post<Trade>('/trades', data),

  update: (id: string, data: any) => api.put<Trade>(`/trades/${id}`, data),

  delete: (id: string) => api.delete(`/trades/${id}`),

  getTags: (accountId?: string) =>
    api.get<string[]>('/trades/tags', { params: { account_id: accountId } }),

  getCustomFieldKeys: (accountId?: string) =>
    api.get<string[]>('/trades/custom-fields/keys', { params: { account_id: accountId } }),
};

// ============================================================
// Metrics API
// ============================================================
export const metricsApi = {
  getMetrics: (accountId: string) =>
    api.get<TradingMetrics>(`/metrics/${accountId}`),
  getEquityCurve: (accountId: string) =>
    api.get<EquityCurvePoint[]>(`/metrics/${accountId}/equity-curve`),
  getDrawdown: (accountId: string) =>
    api.get(`/metrics/${accountId}/drawdown`),
  getSharpe: (accountId: string) =>
    api.get(`/metrics/${accountId}/sharpe`),
};

// ============================================================
// Journal API
// ============================================================
export const journalApi = {
  list: (params?: { account_id?: string; trade_id?: string; tag?: string; search?: string }) =>
    api.get<JournalEntry[]>('/journal', { params }),

  get: (id: string) => api.get<JournalEntry>(`/journal/${id}`),

  create: (data: {
    trade_id?: string | null;
    account_id: string;
    title: string;
    content?: string;
    tags?: string[];
  }) => api.post<JournalEntry>('/journal', data),

  update: (id: string, data: { title?: string; content?: string; tags?: string[] }) =>
    api.put<JournalEntry>(`/journal/${id}`, data),

  delete: (id: string) => api.delete(`/journal/${id}`),

  uploadImage: (id: string, imageUrl: string) =>
    api.post<JournalEntry>(`/journal/${id}/images`, JSON.stringify(imageUrl)),

  removeImage: (id: string, imageUrl: string) =>
    api.post<JournalEntry>(`/journal/${id}/images/remove`, JSON.stringify(imageUrl)),

  getTags: (accountId?: string) =>
    api.get<string[]>('/journal/tags', { params: { account_id: accountId } }),
};

// ============================================================
// ML Analysis API
// ============================================================
export const mlApi = {
  analyze: (accountId: string) =>
    api.get<MlAnalysis>(`/ml/${accountId}/analyze`),
  queryField: (accountId: string, field: string) =>
    api.get(`/ml/${accountId}/query`, { params: { field1: field } }),
  queryCombo: (accountId: string, field1: string, field2: string) =>
    api.get(`/ml/${accountId}/query`, { params: { field1, field2 } }),
};

// ============================================================
// S&P 500 API + Benchmark
// ============================================================
export interface BenchmarkPoint {
  trade_num: number;
  date: string;
  user_equity: number;
  sp500_equity: number;
  user_return_pct: number;
  sp500_return_pct: number;
}

export interface MonthlyComparison {
  month: string;
  user_return_pct: number;
  sp500_return_pct: number;
  alpha: number;
}

export interface SP500DailyPoint {
  date: string;
  close: number;
  sp500_equity: number;
  return_pct: number;
}

export interface BenchmarkComparison {
  account_name: string;
  account_created: string;
  period_days: number;
  start_date: string;
  end_date: string;
  start_capital: number;
  user_total_return_pct: number;
  user_final_equity: number;
  user_total_pnl: number;
  user_sharpe: number;
  user_max_drawdown: number;
  user_total_trades: number;
  user_win_rate: number;
  sp500_total_return_pct: number;
  sp500_final_value: number;
  sp500_total_pnl: number;
  sp500_annualized_return: number;
  sp500_current_price: number;
  sp500_start_price: number;
  alpha: number;
  outperforming: boolean;
  chart_data: BenchmarkPoint[];
  sp500_daily_chart: SP500DailyPoint[];
  monthly_comparison: MonthlyComparison[];
}

export const sp500Api = {
  getCurrentPrice: () => api.get('/sp500/current'),
  getHistoricalData: (accountId?: string) =>
    api.get('/sp500/historical', { params: { account_id: accountId } }),
  getBenchmark: (accountId: string) =>
    api.get<BenchmarkComparison>(`/sp500/benchmark/${accountId}`),
};

// ============================================================
// Auth Types & API
// ============================================================
export interface AuthUser {
  id: string;
  email: string;
  username: string;
  is_verified: boolean;
  created_at: string;
}

export interface AuthResponse {
  token: string;
  user: AuthUser;
}

export interface MessageResponse {
  message: string;
}

export const authApi = {
  register: (email: string, username: string, password: string) =>
    api.post<MessageResponse>('/auth/register', { email, username, password }),
  verify: (email: string, code: string) =>
    api.post<AuthResponse>('/auth/verify', { email, code }),
  resend: (email: string) =>
    api.post<MessageResponse>('/auth/resend', { email }),
  login: (email: string, password: string) =>
    api.post<AuthResponse>('/auth/login', { email, password }),
  me: () => api.get<AuthUser>('/auth/me'),
  updateProfile: (data: { username?: string; email?: string; current_password?: string; new_password?: string }) =>
    api.put<AuthUser>('/auth/me', data),
};


// ============================================================
// AI Chat API
// ============================================================
export interface AiChatRequest {
  provider: string;
  api_key: string;
  model?: string;
  message: string;
  account_id?: string;
}

export interface AiChatResponse {
  reply: string;
  provider: string;
  model: string;
}

export const aiApi = {
  chat: (data: AiChatRequest) =>
    api.post<AiChatResponse>('/ai/chat', data),
};

export default api;
// ============================================================
// Analytics Types (für Dashboard Intelligence & ML Tabs)
// ============================================================
export interface MetricInsight {
  category: string;
  severity: string;
  message: string;
  value: number | null;
  confidence: number;
}

export interface SessionStats {
  session: string;
  total_trades: number;
  win_rate: number;
  avg_pnl: number;
  avg_rr: number;
  avg_rr_wins: number;
  avg_rr_losses: number;
  trades_with_rr: number;
  profit_factor: number;
  payoff_ratio: number;
  kelly_criterion: number;
  calmar_ratio: number;
  recovery_factor: number;
  total_pnl: number;
}

export interface StreakAnalysis {
  max_win_streak: number;
  max_loss_streak: number;
  win_rate_after_3_wins: number;
  win_rate_after_3_losses: number;
  avg_trades_before_loss_streak: number;
}

export interface RRCorrelation {
  high_rr_win_rate: number;
  low_rr_win_rate: number;
  threshold: number;
  high_rr_pnl: number;
  low_rr_pnl: number;
}

export interface AnomalyDetection {
  anomaly_type: string;
  description: string;
  severity: string;
  trade_count: number;
  detected_at: string;
}

export interface DurationAnalysis {
  short_trade_win_rate: number;
  long_trade_win_rate: number;
  median_duration_mins: number;
  avg_win_duration: number;
  avg_loss_duration: number;
}

export interface AnalyticsReport {
  insights: MetricInsight[];
  session_stats: SessionStats[];
  streak_analysis: StreakAnalysis;
  rr_correlation: RRCorrelation | null;
  anomalies: AnomalyDetection[];
  duration_analysis: DurationAnalysis;
}

export interface PatternCondition {
  field: string;
  operator: string;
  value: string;
  contribution: number;
}

export interface DiscoveredPattern {
  pattern_id: string;
  description: string;
  conditions: PatternCondition[];
  win_rate: number;
  sample_size: number;
  avg_rr: number;
  avg_rr_wins: number;
  avg_rr_losses: number;
  trades_with_rr: number;
  profit_factor: number;
  payoff_ratio: number;
  kelly_criterion: number;
  calmar_ratio: number;
  recovery_factor: number;
  total_pnl: number;
  confidence_score: number;
  pattern_type: string;
}

export interface AlgorithmDiscovery {
  detected_patterns: DiscoveredPattern[];
  estimated_algorithm: string;
  primary_edge: string;
  primary_weakness: string;
  overall_confidence: number;
  recommendation: string;
}

export interface RiskCalculationRequest {
  account_balance: number;
  risk_pct: number;
  entry_price: number;
  stop_loss_price: number;
  tick_value?: number;
  tick_size?: number;
}

export interface RiskCalculationResponse {
  account_balance: number;
  risk_pct: number;
  risk_amount: number;
  stop_distance: number;
  stop_distance_pct: number;
  contracts: number;
  take_profit_1r: number;
  take_profit_2r: number;
  take_profit_3r: number;
  reward_1r: number;
  reward_2r: number;
  reward_3r: number;
}

// ============================================================
// Analytics API (Metric Intelligence + Algorithm Discovery)
// ============================================================
export const analyticsApi = {
  getReport: (accountId: string) =>
    api.get<AnalyticsReport>(`/analytics/${accountId}`),
  getAlgorithmDiscovery: (accountId: string) =>
    api.get<AlgorithmDiscovery>(`/analytics/${accountId}/algorithm`),
  calculateRisk: (data: RiskCalculationRequest) =>
    api.post<RiskCalculationResponse>('/analytics/risk/calculate', data),
};


// ============================================================
// Backtest-/Analyse-Dashboard API
// ============================================================
export interface BtKpis {
  trades: number; wins: number; losses: number; wr: number;
  total_pnl: number; profit_factor: number; payoff_ratio: number;
  expectancy: number; avg_win: number; avg_loss: number;
  max_dd_pct: number; max_win_streak: number; max_loss_streak: number;
  longest_underwater: number;
}
export interface BtGroupStat {
  key: string; n: number; wr: number; pnl: number;
  expectancy: number; wr_ci_low: number; wr_ci_high: number;
}
export interface BtClusterStat {
  asset: string; session: string; direction: string;
  n: number; wins: number; wr: number; pnl: number;
  expectancy: number; low_sample: boolean;
}
export interface BtHourlyBlock { block: string; n: number; wr: number; pnl: number; expectancy: number; }
export interface BtHourly { hour: number; n: number; wr: number; pnl: number; expectancy: number; }
export interface BtDistBin { hour: number; count: number; }
export interface BtFactorStat { factor: string; group: string; n: number; wr: number; pnl: number; expectancy: number; }
export interface BtEquityPoint { idx: number; date: string; equity: number; peak: number; dd_pct: number; underwater: number; }
export interface BtDdPhase { range: string; dauer_trades: number; tiefster_dd: number; }
export interface BtMonthly { month: string; trades: number; wins: number; wr: number; pnl: number; equity_ende: number; }

export interface BacktestReport {
  kpis: BtKpis;
  per_asset: BtGroupStat[];
  per_session: BtGroupStat[];
  per_direction: BtGroupStat[];
  per_setup: BtGroupStat[];
  cluster: BtClusterStat[];
  hourly_block: BtHourlyBlock[];
  hourly: BtHourly[];
  distribution: BtDistBin[];
  factors: BtFactorStat[];
  equity_curve: BtEquityPoint[];
  dd_phases: BtDdPhase[];
  monthly: BtMonthly[];
  data_as_of: string;
}

export const backtestApi = {
  /** fromISO / toISO optional im RFC-3339-Format (UTC). */
  get: (accountId: string, fromISO?: string, toISO?: string) => {
    const params: Record<string, string> = {};
    if (fromISO) params.from = fromISO;
    if (toISO) params.to = toISO;
    return api.get<BacktestReport>(`/backtest/${accountId}`, { params });
  },
};



// ============================================================
// Monte Carlo API
// ============================================================
export interface MonteCarloBand {
  trade_num: number;
  p5: number;
  p25: number;
  p50: number;
  p75: number;
  p95: number;
}

export interface MonteCarloResult {
  simulations: number;
  trades_used: number;
  start_capital: number;
  ruin_risk: number;
  survival_rate: number;
  median_final_equity: number;
  best_case_equity: number;
  worst_case_equity: number;
  p5_equity: number;
  p25_equity: number;
  p75_equity: number;
  p95_equity: number;
  worst_case_drawdown: number;
  median_drawdown: number;
  confidence_bands: MonteCarloBand[];
  drawdown_distribution: { percentile: string; max_drawdown_pct: number }[];
  sample_curves: number[][];
}

export const monteCarloApi = {
  simulate: (accountId: string) =>
    api.get<MonteCarloResult>('/montecarlo/' + accountId),
};


// ============================================================
// Extended Analytics API
// ============================================================
export interface MetricAnalysis {
  name: string;
  total_trades: number;
  win_rate: number;
  avg_win_value: number;
  avg_loss_value: number;
  correlation: number;
  optimal_range: string;
  optimal_win_rate: number;
  insight: string;
}

export interface PatternCombo {
  pattern: string;
  trades: number;
  win_rate: number;
  avg_pnl: number;
  edge_vs_global: number;
  confidence: string;
}

export interface ExtendedAnomaly {
  anomaly_type: string;
  metric: string;
  description: string;
  severity: string;
  recommendation: string;
}

export interface ExtendedAnalyticsResult {
  metric_analyses: MetricAnalysis[];
  winning_patterns: PatternCombo[];
  losing_patterns: PatternCombo[];
  anomalies: ExtendedAnomaly[];
  optimal_profile: Record<string, string>;
  global_win_rate: number;
}

export const extendedAnalyticsApi = {
  get: (accountId: string) =>
    api.get<ExtendedAnalyticsResult>('/analytics/extended/' + accountId),
};

// ============================================================
// P&L Overview (Bybit-Style Report) — aktiver Account, ein Call
// ============================================================
export interface PnlCurvePoint {
  date: string;            // YYYY-MM-DD (UTC)
  cumulative_pnl: number;
}

export interface PnlRankingPoint {
  symbol: string;
  pnl: number;
}

export interface PnlOverview {
  total_pnl: number;
  trading_volume: number;
  volume_available: boolean;
  data_as_of: string;      // RFC-3339 UTC
  curve: PnlCurvePoint[];
  ranking: PnlRankingPoint[];
}

export const pnlApi = {
  /** fromISO / toISO im RFC-3339-Format (UTC), z.B. 2026-03-01T00:00:00Z */
  overview: (fromISO: string, toISO: string) =>
    api.get<PnlOverview>('/pnl/overview', { params: { from: fromISO, to: toISO } }),
};
